import { useState, useEffect, useCallback, useRef } from 'react';
import { MAX_LISTING_IMAGES, FEATURED_MARKET_CODES } from '../config/constants';
import { getCountry, inferCountryFromLocation, resolveListingCountryClient } from '../data/countries';
import { getUserDisplayName } from '../utils/properties';
import {
  resolveShareStructureClient,
  validateRaisingStructureClient,
} from '../utils/submissions';
import { buildOfferingApiBody } from '../utils/properties';
import {
  filterNumericInput,
  sha256File,
  uploadListingPhotos,
  uploadListingVideo,
  emptyRentCostFormFields,
  emptyNearbyFormFields,
  buildRentMonthlyCostsApiBody,
  buildNearbyApiBody,
} from '../utils/submissions';
import NearbyAmenitiesFields from '../components/forms/NearbyAmenitiesFields';
import PropertyLocationMap from '../components/shared/PropertyLocationMap';
import RentMonthlyCostsFields from '../components/forms/RentMonthlyCostsFields';
import AdminOfferingDetailsFields from '../components/forms/AdminOfferingDetailsFields';
import PipelineProgress from '../components/submission/PipelineProgress';
import PageRefinedHero from '../components/layout/PageRefinedHero';
import {
  getYouTubeThumbnail,
  getYouTubeWatchUrl,
  isYouTubeUrl,
  normalizeYouTubeUrl,
} from '../utils/youtube';
import {
  ListFormCard,
  ListFormCollapsible,
  ListFormField,
  ListFormToggle,
  listV2InputClass,
  listV2SelectClass,
  listV2TextareaClass,
} from '../components/listing/ListFormUi';

const ListPropertyPage = ({ onSubmit, user, addToast, setPage }) => {
  const [form, setForm] = useState({
    title: '', location: '', country: 'US', type: 'Residential', price: '', description: '',
    raisingMode: 'co_owners',
    shareCount: '10',
    minInvestment: '25000',
    agentName: user ? getUserDisplayName(user) : '',
    agentCompany: '', agentEmail: user?.email || '', agentPhone: '',
    yearBuilt: '', sqft: '', beds: '', baths: '', monthlyRent: '', occupancy: '', annualOperatingExpenses: '',
    renovationNeeded: 'no', renovationCost: '', renovationDesc: '', landBanking: 'no',
    fundingDeadline: '', projectedTimeline: '', riskRating: '', secOfferingType: '',
    propertyManagerName: '', propertyManagerLicenseReference: '',
    developerName: '', developerTrackRecord: '',
    ...emptyRentCostFormFields(),
    ...emptyNearbyFormFields(),
  });
  const [photos, setPhotos] = useState([]);
  const [video, setVideo] = useState('');
  const [videoFileName, setVideoFileName] = useState('');
  const [youtubeInput, setYoutubeInput] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submittedPipeline, setSubmittedPipeline] = useState({ status: 'listed', title: '', location: '', price: '', listingMode: 'sale' });
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [videoDragOver, setVideoDragOver] = useState(false);
  const [listingModePrompt, setListingModePrompt] = useState(false);
  const [submittingListing, setSubmittingListing] = useState(false);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const update = (field, val) => setForm((prev) => ({ ...prev, [field]: val }));

  useEffect(() => {
    if (!user) return;
    setForm((prev) => ({
      ...prev,
      agentName: prev.agentName || getUserDisplayName(user),
      agentEmail: prev.agentEmail || user.email || '',
    }));
  }, [user]);

  const processImageFiles = useCallback(async (rawFiles) => {
    const files = Array.from(rawFiles || []).filter((f) => f.type.startsWith('image/'));
    if (!files.length) return;
    const room = MAX_LISTING_IMAGES - photos.length;
    if (room <= 0) {
      addToast(`Maximum ${MAX_LISTING_IMAGES} photos per listing.`, 'error');
      return;
    }
    const existingHashes = new Set(photos.map((p) => p.hash));
    const toUpload = [];
    for (const file of files) {
      if (toUpload.length >= room) break;
      const hash = await sha256File(file);
      if (existingHashes.has(hash)) {
        addToast(`"${file.name}" is already in this listing.`, 'error');
        continue;
      }
      existingHashes.add(hash);
      toUpload.push({ file, hash });
    }
    if (!toUpload.length) return;

    if (!user && !firebase.auth().currentUser) {
      addToast('Please sign in to upload photos and track your listing.', 'error');
      if (setPage) setPage('login');
      return;
    }

    setUploading(true);
    try {
      const urls = await uploadListingPhotos(toUpload.map((t) => t.file));
      if (!urls.length) {
        addToast('No new photos were added.', 'error');
        return;
      }
      const added = urls.map((url, i) => ({ url, hash: toUpload[i]?.hash || url }));
      setPhotos((prev) => [...prev, ...added].slice(0, MAX_LISTING_IMAGES));
      addToast(`${urls.length} photo(s) uploaded.`);
    } catch (err) {
      addToast(err.message || 'Photo upload failed.', 'error');
    } finally {
      setUploading(false);
    }
  }, [photos, user, addToast, setPage]);

  const handleImages = async (e) => {
    await processImageFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    await processImageFiles(e.dataTransfer.files);
  };

  const removeImage = (url) => setPhotos((prev) => prev.filter((p) => p.url !== url));

  const processVideoFile = useCallback(async (rawFile) => {
    const file = Array.isArray(rawFile) ? rawFile[0] : rawFile;
    if (!file) return;

    if (!user && !firebase.auth().currentUser) {
      addToast('Please sign in to upload videos and track your listing.', 'error');
      if (setPage) setPage('login');
      return;
    }

    setUploadingVideo(true);
    try {
      const url = await uploadListingVideo(file);
      if (!url) {
        addToast('Video upload failed.', 'error');
        return;
      }
      setVideo(url);
      setVideoFileName(file.name || 'property-video.mp4');
      setYoutubeInput('');
      addToast('Property video uploaded.');
    } catch (err) {
      addToast(err.message || 'Video upload failed.', 'error');
    } finally {
      setUploadingVideo(false);
    }
  }, [user, addToast, setPage]);

  const handleVideoSelect = async (e) => {
    await processVideoFile(e.target.files?.[0]);
    e.target.value = '';
  };

  const handleVideoDrop = async (e) => {
    e.preventDefault();
    setVideoDragOver(false);
    await processVideoFile(e.dataTransfer.files?.[0]);
  };

  const removeVideo = () => {
    setVideo('');
    setVideoFileName('');
    setYoutubeInput('');
  };

  const applyYouTubeLink = () => {
    const normalized = normalizeYouTubeUrl(youtubeInput);
    if (!normalized) {
      addToast('Enter a valid YouTube link (youtube.com or youtu.be).', 'error');
      return;
    }
    setVideo(normalized);
    setVideoFileName('YouTube video');
    addToast('YouTube link added to your listing.');
  };

  const priceNum = parseFloat(form.price) || 0;
  const sharePreview = resolveShareStructureClient({
    price: priceNum,
    raisingMode: form.raisingMode,
    shareCount: form.shareCount,
    minInvestment: form.minInvestment,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (photos.length === 0) {
      addToast('Please upload at least one property photo before submitting.', 'error');
      return;
    }
    setListingModePrompt(true);
  };

  const finalizeListingSubmit = async (listingMode) => {
    if (listingMode !== 'rent') {
      const check = validateRaisingStructureClient({
        price: priceNum,
        raisingMode: form.raisingMode,
        shareCount: form.shareCount,
        minInvestment: form.minInvestment,
      });
      if (!check.ok) {
        addToast(check.message, 'error');
        return;
      }
    }
    setSubmittingListing(true);
    try {
      const country = resolveListingCountryClient(form.country, form.location);
      const raisingPayload =
        listingMode === 'rent'
          ? {}
          : (() => {
              const r = resolveShareStructureClient({
                price: priceNum,
                raisingMode: form.raisingMode,
                shareCount: form.shareCount,
                minInvestment: form.minInvestment,
              });
              return {
                raisingMode: r.raisingMode,
                shareCount: r.shareCount,
                totalShares: r.shares,
                minInvestment: r.minInvestment,
              };
            })();
      const offeringPayload =
        listingMode === 'sale'
          ? buildOfferingApiBody(form)
          : {};
      const hasOffering =
        listingMode === 'sale' &&
        Object.values(offeringPayload).some((v) => String(v || '').trim() !== '');
      const rentCostsPayload =
        listingMode === 'rent' ? buildRentMonthlyCostsApiBody(form) : {};
      const result = await onSubmit({
        ...form,
        country,
        images: photos.map((p) => p.url),
        video: video || '',
        listingMode,
        ...raisingPayload,
        ...(hasOffering ? offeringPayload : {}),
        ...rentCostsPayload,
        ...buildNearbyApiBody(form),
      });
      setSubmittedPipeline({
        status: result?.status || 'listed',
        title: form.title,
        location: form.location,
        price: form.price,
        listingMode: listingMode === 'rent' ? 'rent' : 'sale',
      });
      setListingModePrompt(false);
      setSubmitted(true);
    } catch (err) {
      addToast(err.message || 'Could not submit property. Please try again.', 'error');
    } finally {
      setSubmittingListing(false);
    }
  };

  if (submitted) {
    return (
      <div className="coown-page coown-page--hero list-page list-page--v2">
        <div className="list-v2-shell" style={{ paddingTop: '2.5rem' }}>
          <div className="list-v2-card list-v2-success">
            <i className="fas fa-check-circle list-v2-success__icon" aria-hidden="true"></i>
            <h2>Property submitted</h2>
            <p className="list-v2-success__lead">
              Thank you for listing with CoOwn. Your submission is now under review.
              {submittedPipeline.listingMode === 'rent'
                ? ' When approved, it will appear in rental browse results.'
                : ' When approved, it can be published for fractional investment on the home page.'}
            </p>
            {submittedPipeline.title && (
              <div className="list-v2-success__summary">
                <div className="submission-row-title-row" style={{ marginBottom: '0.35rem' }}>
                  <h4 className="submission-row-title-text" style={{ margin: 0 }}>{submittedPipeline.title}</h4>
                  <span className={`status-badge listing-mode-badge status-${submittedPipeline.listingMode === 'rent' ? 'pending' : 'approved'}`}>
                    {submittedPipeline.listingMode === 'rent' ? 'For rent' : 'For sale (buy shares)'}
                  </span>
                </div>
                <p style={{ color: 'var(--text-light)', fontSize: '0.9rem', margin: 0 }}>
                  <i className="fas fa-map-marker-alt" style={{ color: 'var(--primary)', marginRight: '0.35rem' }}></i>
                  {submittedPipeline.location}
                  {submittedPipeline.price ? ` · $${Number(submittedPipeline.price).toLocaleString()}` : ''}
                </p>
              </div>
            )}
            <PropertyLocationMap location={submittedPipeline.location} height={260} title="Your property on the map" />
            <div className="list-v2-success__pipeline">
              <h4><i className="fas fa-route"></i> What happens next?</h4>
              <PipelineProgress currentStatus={submittedPipeline.status} />
            </div>
            <p className="list-v2-success__footnote">
              Stages turn green as our team advances your listing. Open <strong>My Listings</strong> anytime to see full history.
            </p>
            <div className="list-v2-success__actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setSubmitted(false);
                  setPhotos([]);
                  setVideo('');
                  setVideoFileName('');
                  setYoutubeInput('');
                  setSubmittedPipeline({ status: 'listed', title: '', location: '', price: '' });
                }}
              >
                Submit another property
              </button>
              {setPage && (
                <button type="button" className="btn btn-outline" onClick={() => setPage('agent-dashboard')}>
                  <i className="fas fa-list-alt"></i> View My Listings
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="coown-page coown-page--hero list-page list-page--v2">
      <PageRefinedHero
        badge="List with CoOwn"
        title="List your"
        titleAccent="property"
        subtitle="Reach fractional investors or renters worldwide. Our team reviews every submission before it goes live."
        benefits={[
          { icon: 'fas fa-chart-pie', title: 'Fractional sale', subtitle: 'Pool capital from CoOwn investors' },
          { icon: 'fas fa-key', title: 'List for rent', subtitle: 'Appear in rental browse results' },
          { icon: 'fas fa-shield-alt', title: 'Reviewed listing', subtitle: 'Admin checks quality before publish' },
        ]}
      />

      <div className="list-v2-shell list-v2-form-wrap">
        {listingModePrompt && (
          <div className="modal-overlay active" onClick={(e) => e.target === e.currentTarget && !submittingListing && setListingModePrompt(false)}>
            <div className="modal list-v2-modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>How do you want to list?</h2>
                <button type="button" className="modal-close" disabled={submittingListing} onClick={() => setListingModePrompt(false)}>&times;</button>
              </div>
              <div className="modal-body">
                <p style={{ color: 'var(--text-light)', marginBottom: '1.25rem' }}>
                  Choose whether you are offering the property for <strong>rent</strong> or for <strong>fractional sale</strong> (CoOwn investors).
                </p>
                <button type="button" className="btn btn-outline list-v2-modal-choice" disabled={submittingListing} onClick={() => finalizeListingSubmit('rent')}>
                  <i className="fas fa-key"></i>
                  <span>
                    <strong>List for rent</strong>
                    <small>Appears on Rent page</small>
                  </span>
                </button>
                <button type="button" className="btn btn-primary list-v2-modal-choice" disabled={submittingListing} onClick={() => finalizeListingSubmit('sale')}>
                  <i className="fas fa-chart-pie"></i>
                  <span>
                    <strong>Sell shares</strong>
                    <small>Fractional investment on home page</small>
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="list-v2-form">
          <ListFormCard icon="building" title="Property information" badge="Required" badgeTone="required">
            <ListFormField label="Property title" required htmlFor="list-title">
              <input
                id="list-title"
                className={listV2InputClass}
                required
                placeholder="e.g. Downtown Luxury Condo"
                value={form.title}
                onChange={(e) => update('title', e.target.value)}
              />
            </ListFormField>

            <ListFormField
              label="Location"
              required
              htmlFor="list-location"
              helper="Include the country in the address. We use it for the map flag and market filters."
            >
              <input
                id="list-location"
                className={listV2InputClass}
                required
                placeholder="City, state, country — e.g. Lekki, Lagos, Nigeria"
                value={form.location}
                onChange={(e) => update('location', e.target.value)}
                onBlur={() => {
                  const inferred = inferCountryFromLocation(form.location);
                  if (inferred && inferred !== form.country) {
                    update('country', inferred);
                  }
                }}
              />
            </ListFormField>

            <PropertyLocationMap location={form.location} height={240} title="Location preview" />

            <div className="list-v2-grid list-v2-grid--3">
              <ListFormField label="Country" required htmlFor="list-country">
                <select
                  id="list-country"
                  className={listV2SelectClass}
                  value={form.country}
                  onChange={(e) => update('country', e.target.value)}
                >
                  {FEATURED_MARKET_CODES.map((code) => (
                    <option key={code} value={code}>{getCountry(code).name} ({code})</option>
                  ))}
                </select>
              </ListFormField>
              <ListFormField label="Property type" required htmlFor="list-type">
                <select id="list-type" className={listV2SelectClass} value={form.type} onChange={(e) => update('type', e.target.value)}>
                  <option>Residential</option>
                  <option>Commercial</option>
                  <option>Vacation</option>
                  <option>Land</option>
                </select>
              </ListFormField>
              <ListFormField label="Total property value ($)" required htmlFor="list-price">
                <input
                  id="list-price"
                  className={listV2InputClass}
                  type="text"
                  inputMode="decimal"
                  required
                  placeholder="450000"
                  value={form.price}
                  onChange={(e) => update('price', filterNumericInput(e.target.value, { decimal: true }))}
                />
              </ListFormField>
            </div>

            <div className="list-v2-inset list-v2-inset--accent">
              <h3 className="list-v2-inset__title">
                <i className="fas fa-chart-pie"></i>
                Suggest how to divide shares
                <span className="list-v2-inset__tag">Fractional sale</span>
              </h3>
              <p className="list-v2-inset__text">
                This is your <strong>suggestion only</strong>. CoOwn admins set the final share count and minimum investment before your listing goes Live on Site.
              </p>
              <div className="list-v2-radio-group">
                <label className="list-v2-radio">
                  <input
                    type="radio"
                    name="raisingMode"
                    checked={form.raisingMode !== 'min_investment'}
                    onChange={() => update('raisingMode', 'co_owners')}
                  />
                  <span>
                    <strong>Number of co-owners</strong>
                    <small>e.g. 10 people each buy one share</small>
                  </span>
                </label>
                <label className="list-v2-radio">
                  <input
                    type="radio"
                    name="raisingMode"
                    checked={form.raisingMode === 'min_investment'}
                    onChange={() => update('raisingMode', 'min_investment')}
                  />
                  <span>
                    <strong>Minimum investment</strong>
                    <small>e.g. $25,000 per investor; we divide shares from price</small>
                  </span>
                </label>
              </div>
              {form.raisingMode === 'min_investment' ? (
                <ListFormField label="Minimum investment per share ($)" htmlFor="list-min-invest">
                  <input
                    id="list-min-invest"
                    className={listV2InputClass}
                    type="text"
                    inputMode="decimal"
                    placeholder="25000"
                    value={form.minInvestment}
                    onChange={(e) => update('minInvestment', filterNumericInput(e.target.value, { decimal: true }))}
                  />
                </ListFormField>
              ) : (
                <ListFormField label="Number of co-owners (total shares)" htmlFor="list-share-count">
                  <input
                    id="list-share-count"
                    className={listV2InputClass}
                    type="text"
                    inputMode="numeric"
                    placeholder="10"
                    value={form.shareCount}
                    onChange={(e) => update('shareCount', filterNumericInput(e.target.value))}
                  />
                </ListFormField>
              )}
              {priceNum > 0 && (
                <p className="list-v2-share-preview">
                  Suggested preview: {sharePreview.shares.toLocaleString()} shares at about $
                  {(priceNum / sharePreview.shares).toLocaleString(undefined, { maximumFractionDigits: 0 })} each
                  {form.raisingMode === 'min_investment'
                    ? ` (min. investment $${Number(sharePreview.minInvestment).toLocaleString()})`
                    : ` (min. investment $${Number(sharePreview.minInvestment).toLocaleString()} per share)`}
                </p>
              )}
            </div>

            <ListFormField label="Description" required htmlFor="list-description">
              <textarea
                id="list-description"
                className={listV2TextareaClass}
                required
                rows={4}
                placeholder="Describe the property, highlights, and neighborhood..."
                value={form.description}
                onChange={(e) => update('description', e.target.value)}
              />
            </ListFormField>
          </ListFormCard>

          <ListFormCard icon="images" title="Property photos" badge="Required" badgeTone="required">
            <p className="list-v2-hint">
              Upload at least one photo (up to {MAX_LISTING_IMAGES}, JPEG or PNG). Duplicate images in one listing are not allowed.
            </p>
            <div
              className={`list-v2-dropzone${dragOver ? ' is-dragover' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => !uploading && photos.length < MAX_LISTING_IMAGES && fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !uploading && photos.length < MAX_LISTING_IMAGES) {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              role="button"
              tabIndex={0}
              aria-label="Upload property photos"
            >
              <span className="list-v2-dropzone__icon"><i className={`fas fa-${uploading ? 'spinner fa-spin' : 'cloud-upload-alt'}`}></i></span>
              <p className="list-v2-dropzone__title">{uploading ? 'Uploading…' : 'Drop photos here or click to upload'}</p>
              <p className="list-v2-dropzone__meta">JPEG or PNG · {photos.length} / {MAX_LISTING_IMAGES}</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                hidden
                disabled={uploading || photos.length >= MAX_LISTING_IMAGES}
                onChange={handleImages}
              />
            </div>
            {photos.length > 0 && (
              <div className="list-v2-photo-grid">
                {photos.map((p, idx) => (
                  <div key={p.url} className="list-v2-photo-thumb">
                    {idx === 0 ? <span className="list-v2-photo-cover">Cover</span> : null}
                    <img src={p.url} alt="" />
                    <button type="button" className="list-v2-photo-remove" onClick={() => removeImage(p.url)} aria-label="Remove photo">
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </ListFormCard>

          <ListFormCard icon="video" title="Property video" badge="Optional">
            <p className="list-v2-hint">
              Upload a walkthrough file or paste a YouTube link. File uploads (MP4, MOV, AVI, MKV, MPG · max 100 MB) can be downloaded from your listing; YouTube links open on YouTube with a thumbnail preview.
            </p>
            {!video ? (
              <>
                <div
                  className={`list-v2-dropzone${videoDragOver ? ' is-dragover' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); setVideoDragOver(true); }}
                  onDragLeave={() => setVideoDragOver(false)}
                  onDrop={handleVideoDrop}
                  onClick={() => !uploadingVideo && videoInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && !uploadingVideo) {
                      e.preventDefault();
                      videoInputRef.current?.click();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label="Upload property video"
                >
                  <span className="list-v2-dropzone__icon"><i className={`fas fa-${uploadingVideo ? 'spinner fa-spin' : 'film'}`}></i></span>
                  <p className="list-v2-dropzone__title">{uploadingVideo ? 'Uploading video…' : 'Drop a video here or click to upload'}</p>
                  <p className="list-v2-dropzone__meta">MP4, MOV, AVI, MKV, MPG · up to 100 MB</p>
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept=".mp4,.mov,.avi,.mkv,.mpg,.mpeg,video/mp4,video/quicktime,video/x-msvideo,video/x-matroska,video/mpeg,video/*"
                    hidden
                    disabled={uploadingVideo}
                    onChange={handleVideoSelect}
                  />
                </div>
                <div className="list-v2-or-divider" aria-hidden="true"><span>or</span></div>
                <ListFormField label="YouTube link" htmlFor="list-youtube-url" helper="Paste a public YouTube URL for your property tour.">
                  <div className="list-v2-youtube-input-row">
                    <input
                      id="list-youtube-url"
                      className={listV2InputClass}
                      type="url"
                      placeholder="https://www.youtube.com/watch?v=..."
                      value={youtubeInput}
                      onChange={(e) => setYoutubeInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          applyYouTubeLink();
                        }
                      }}
                    />
                    <button type="button" className="btn btn-primary list-v2-youtube-add" onClick={applyYouTubeLink} disabled={!youtubeInput.trim()}>
                      <i className="fab fa-youtube"></i> Add link
                    </button>
                  </div>
                </ListFormField>
              </>
            ) : isYouTubeUrl(video) ? (
              <div className="list-v2-video-preview list-v2-video-preview--youtube">
                <a
                  href={getYouTubeWatchUrl(video)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="list-v2-youtube-preview"
                >
                  <img src={getYouTubeThumbnail(video)} alt="" />
                  <span className="list-v2-youtube-preview__overlay">
                    <i className="fab fa-youtube"></i>
                    <span>Watch on YouTube</span>
                  </span>
                </a>
                <div className="list-v2-video-preview__bar">
                  <p className="list-v2-video-preview__name" title={video}>{videoFileName}</p>
                  <button type="button" className="list-v2-video-remove" onClick={removeVideo}>
                    <i className="fas fa-trash-alt"></i> Remove
                  </button>
                </div>
              </div>
            ) : (
              <div className="list-v2-video-preview">
                <video src={video} controls preload="metadata" />
                <div className="list-v2-video-preview__bar">
                  <p className="list-v2-video-preview__name" title={videoFileName}>{videoFileName || 'Property video'}</p>
                  <button type="button" className="list-v2-video-remove" onClick={removeVideo}>
                    <i className="fas fa-trash-alt"></i> Remove
                  </button>
                </div>
              </div>
            )}
          </ListFormCard>

          <ListFormCard icon="ruler-combined" title="Property details" badge="Required" badgeTone="required">
            <div className="list-v2-grid list-v2-grid--2">
              <ListFormField label="Year built" htmlFor="list-year">
                <input id="list-year" className={listV2InputClass} type="text" inputMode="numeric" placeholder="2019" value={form.yearBuilt} onChange={(e) => update('yearBuilt', filterNumericInput(e.target.value))} />
              </ListFormField>
              <ListFormField label="Square feet" htmlFor="list-sqft">
                <input id="list-sqft" className={listV2InputClass} type="text" inputMode="numeric" placeholder="1200" value={form.sqft} onChange={(e) => update('sqft', filterNumericInput(e.target.value))} />
              </ListFormField>
              <ListFormField label="Bedrooms" htmlFor="list-beds">
                <input id="list-beds" className={listV2InputClass} type="text" inputMode="numeric" placeholder="2" value={form.beds} onChange={(e) => update('beds', filterNumericInput(e.target.value))} />
              </ListFormField>
              <ListFormField label="Bathrooms" htmlFor="list-baths">
                <input id="list-baths" className={listV2InputClass} type="text" inputMode="numeric" placeholder="2" value={form.baths} onChange={(e) => update('baths', filterNumericInput(e.target.value))} />
              </ListFormField>
            </div>
            <ListFormField
              label="Base monthly rent"
              htmlFor="list-rent"
              helper="Required for rent listings. Optional for fractional sale."
            >
              <input id="list-rent" className={listV2InputClass} type="text" inputMode="decimal" placeholder="3200" value={form.monthlyRent} onChange={(e) => update('monthlyRent', filterNumericInput(e.target.value, { decimal: true }))} />
            </ListFormField>
            <div className="list-v2-grid list-v2-grid--2">
              <ListFormField label="Occupancy rate (%)" htmlFor="list-occupancy">
                <input id="list-occupancy" className={listV2InputClass} type="text" inputMode="decimal" placeholder="e.g. 94" value={form.occupancy} onChange={(e) => update('occupancy', filterNumericInput(e.target.value, { decimal: true }))} />
              </ListFormField>
              <ListFormField label="Est. annual operating expenses ($)" htmlFor="list-opex">
                <input id="list-opex" className={listV2InputClass} type="text" inputMode="decimal" placeholder="e.g. 12000" value={form.annualOperatingExpenses} onChange={(e) => update('annualOperatingExpenses', filterNumericInput(e.target.value, { decimal: true }))} />
              </ListFormField>
            </div>
          </ListFormCard>

          <ListFormCollapsible
            icon="map-marker-alt"
            title="Nearby landmarks & amenities"
            badge="Optional"
            hint='Optional travel times or distances (e.g. "15 min (JFK)", "5 min"). Shown on the public listing after approval.'
          >
            <NearbyAmenitiesFields form={form} update={update} />
          </ListFormCollapsible>

          <ListFormCollapsible
            icon="receipt"
            title="Monthly costs"
            badge="Optional"
            hint="If you list for rent, you can break down typical monthly costs. Renters see this on the public listing after approval."
          >
            <RentMonthlyCostsFields form={form} setForm={setForm} idPrefix="list-rent-costs" variant="compact" />
          </ListFormCollapsible>

          <ListFormCard icon="tools" title="Renovation & land banking">
            <ListFormToggle
              label="Renovation needed?"
              description="Property requires renovation work before listing"
              checked={form.renovationNeeded === 'yes'}
              onChange={(on) => update('renovationNeeded', on ? 'yes' : 'no')}
            />
            {form.renovationNeeded === 'yes' && (
              <>
                <ListFormField label="Estimated cost ($)" htmlFor="list-reno-cost">
                  <input id="list-reno-cost" className={listV2InputClass} type="text" inputMode="decimal" placeholder="25000" value={form.renovationCost} onChange={(e) => update('renovationCost', filterNumericInput(e.target.value, { decimal: true }))} />
                </ListFormField>
                <ListFormField label="Renovation description" htmlFor="list-reno-desc">
                  <textarea id="list-reno-desc" className={listV2TextareaClass} rows={3} placeholder="Describe what needs renovation..." value={form.renovationDesc} onChange={(e) => update('renovationDesc', e.target.value)} />
                </ListFormField>
              </>
            )}
            <ListFormToggle
              label="Land banking opportunity?"
              description="Property held for future appreciation without immediate development"
              checked={form.landBanking === 'yes'}
              onChange={(on) => update('landBanking', on ? 'yes' : 'no')}
            />
          </ListFormCard>

          <ListFormCollapsible
            icon="clipboard-list"
            title="Offering details"
            badge="Optional"
            hint="For fractional sale listings only. If you fill these in, investors will see them on the public property page after admin approval."
          >
            <AdminOfferingDetailsFields form={form} setForm={setForm} idPrefix="list-offering" />
          </ListFormCollapsible>

          <ListFormCard icon="user-tie" title="Agent / company" badge="Required" badgeTone="required">
            <div className="list-v2-grid list-v2-grid--2">
              <ListFormField label="Agent name" required htmlFor="list-agent-name">
                <input id="list-agent-name" className={listV2InputClass} required placeholder="Full name" value={form.agentName} onChange={(e) => update('agentName', e.target.value)} />
              </ListFormField>
              <ListFormField label="Company name" required htmlFor="list-agent-company">
                <input id="list-agent-company" className={listV2InputClass} required placeholder="Realty company" value={form.agentCompany} onChange={(e) => update('agentCompany', e.target.value)} />
              </ListFormField>
              <ListFormField label="Email" required htmlFor="list-agent-email">
                <input id="list-agent-email" className={listV2InputClass} type="email" required placeholder="agent@company.com" value={form.agentEmail} onChange={(e) => update('agentEmail', e.target.value)} />
              </ListFormField>
              <ListFormField label="Phone" required htmlFor="list-agent-phone">
                <input id="list-agent-phone" className={listV2InputClass} required placeholder="(555) 123-4567" value={form.agentPhone} onChange={(e) => update('agentPhone', e.target.value)} />
              </ListFormField>
            </div>
          </ListFormCard>

          <div className="list-v2-sticky-footer">
            <p className="list-v2-sticky-footer__legal">
              By submitting, you agree that CoOwn may publish a <strong>platform listing price</strong> for investors that differs from your ask (see{' '}
              <button type="button" className="list-v2-inline-link" onClick={() => setPage?.('terms')}>Terms of Service</button>).
            </p>
            <button type="submit" className="btn btn-primary list-v2-submit" disabled={uploading || uploadingVideo || photos.length === 0}>
              <i className="fas fa-paper-plane"></i> Submit property for review
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ListPropertyPage;
